import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChevronUp, ChevronDown, Grid, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import { useSceneStore } from '../../store/sceneStore';
import { CurveCanvas } from './CurveCanvas';
import { EasingPresets } from './EasingPresets';

interface CurveEditorProps {
  className?: string;
}

const MIN_HEIGHT = 100;
const MAX_HEIGHT = 500;
const COLLAPSED_HEIGHT = 32;

export const CurveEditor: React.FC<CurveEditorProps> = ({ className }) => {
  const {
    showCurveEditor,
    curveEditorHeight,
    toggleCurveEditor,
    setCurveEditorHeight,
    showGrid,
    toggleGrid,
    selectedAnimationId,
    selectedKeyframeIds,
    animations,
  } = useAnimationStore();

  const { gameObjects } = useSceneStore();

  const [isMaximized, setIsMaximized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 200 });
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const resizeStartRef = useRef({ y: 0, height: 0 });

  // Get the selected animation
  const selectedAnimation = selectedAnimationId ? animations[selectedAnimationId] : null;

  // Find the selected keyframe and its track
  const { selectedKeyframe, selectedTrack } = useMemo(() => {
    if (!selectedAnimation || selectedKeyframeIds.length === 0) {
      return { selectedKeyframe: null, selectedTrack: null };
    }

    const keyframeId = selectedKeyframeIds[0]; // Use first selected keyframe

    for (const track of selectedAnimation.tracks) {
      const keyframe = track.keyframes.find((kf) => kf.id === keyframeId);
      if (keyframe) {
        return { selectedKeyframe: keyframe, selectedTrack: track };
      }
    }

    return { selectedKeyframe: null, selectedTrack: null };
  }, [selectedAnimation, selectedKeyframeIds]);

  // Get target object name for display
  const targetObjectName = useMemo(() => {
    if (!selectedTrack) return null;
    const obj = gameObjects[selectedTrack.targetObjectId];
    return obj?.name || 'Unknown Object';
  }, [selectedTrack, gameObjects]);

  // Handle resize drag
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = { y: e.clientY, height: curveEditorHeight };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = resizeStartRef.current.y - moveEvent.clientY;
      const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, resizeStartRef.current.height + deltaY));
      setCurveEditorHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [curveEditorHeight, setCurveEditorHeight]);

  // Toggle maximize
  const handleToggleMaximize = useCallback(() => {
    setIsMaximized((prev) => !prev);
  }, []);

  // Update canvas size when container resizes
  useEffect(() => {
    if (!canvasContainerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width, height } = entry.contentRect;
        setCanvasSize({
          width: Math.max(100, width - 4), // Account for padding
          height: Math.max(100, height - 4),
        });
      }
    });

    resizeObserver.observe(canvasContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [showCurveEditor, isMaximized]);

  // Determine the actual height
  const actualHeight = isMaximized ? MAX_HEIGHT : curveEditorHeight;

  // Collapsed state
  if (!showCurveEditor) {
    return (
      <div
        className={cn(
          'bg-zinc-900 border-t border-zinc-700',
          className
        )}
        style={{ height: COLLAPSED_HEIGHT }}
      >
        <button
          className="w-full h-full flex items-center justify-center gap-2 text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
          onClick={toggleCurveEditor}
        >
          <ChevronUp size={14} />
          <span>Show Curve Editor</span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-zinc-900 border-t border-zinc-700 flex flex-col',
        isResizing && 'select-none',
        className
      )}
      style={{ height: actualHeight }}
    >
      {/* Resize Handle */}
      <div
        className={cn(
          'h-1 cursor-ns-resize bg-zinc-800 hover:bg-blue-600 transition-colors flex-shrink-0',
          isResizing && 'bg-blue-600'
        )}
        onMouseDown={handleResizeStart}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-zinc-300">Curve Editor</span>
          {selectedKeyframe && selectedTrack && (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: selectedTrack.color }}
              />
              <span>{targetObjectName}</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-400">{selectedKeyframe.easing}</span>
              <span className="text-zinc-600">@</span>
              <span className="font-mono text-zinc-400">{selectedKeyframe.time.toFixed(2)}s</span>
            </div>
          )}
          {!selectedKeyframe && (
            <span className="text-xs text-zinc-500 italic">No keyframe selected</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Grid toggle */}
          <button
            className={cn(
              'p-1 rounded transition-colors',
              showGrid ? 'text-blue-400 bg-blue-500/20' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
            )}
            onClick={toggleGrid}
            title={showGrid ? 'Hide grid' : 'Show grid'}
          >
            <Grid size={14} />
          </button>

          {/* Maximize toggle */}
          <button
            className={cn(
              'p-1 rounded transition-colors',
              isMaximized ? 'text-blue-400 bg-blue-500/20' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
            )}
            onClick={handleToggleMaximize}
            title={isMaximized ? 'Restore size' : 'Maximize'}
          >
            {isMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>

          {/* Collapse button */}
          <button
            className="p-1 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
            onClick={toggleCurveEditor}
            title="Hide curve editor"
          >
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas area */}
        <div
          ref={canvasContainerRef}
          className="flex-1 p-0.5 overflow-hidden"
        >
          <CurveCanvas
            width={canvasSize.width}
            height={canvasSize.height}
            showGrid={showGrid}
            selectedKeyframe={selectedKeyframe}
            selectedTrack={selectedTrack}
            animationId={selectedAnimationId}
          />
        </div>

        {/* Easing presets sidebar */}
        <EasingPresets
          selectedKeyframe={selectedKeyframe}
          selectedTrack={selectedTrack}
          animationId={selectedAnimationId}
          disabled={!selectedKeyframe}
        />
      </div>
    </div>
  );
};

export default CurveEditor;
